import React, { forwardRef } from "react";
import {
  DatePicker,
  DateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { FormControl, FormHelperText } from "@material-ui/core";
import { getPointerEventsStyles, getReadOnlyStyles } from "../../lib/styles";
import { DATE_TIME_FORMAT, DATE_FORMAT } from "../../lib/constants";
import { parseDateOnly, formatDateOnly } from "../../../../utils";

export const DateInput = forwardRef(
  ({ fieldConfig, controllerField, fieldState }, ref) => {
    const { type, readOnly } = fieldConfig;
    const { value, onChange, ...rest } = controllerField;

    const PickerComponent = type === "dateTime" ? DateTimePicker : DatePicker;
    const formatString = type === "dateTime" ? DATE_TIME_FORMAT : DATE_FORMAT;
    // Keep the toolbar: its year button is the only way to jump to a different
    // year in @material-ui/pickers v3, and its AM/PM toggle is the only
    // meridiem control (the clock view has none). Without it, historical
    // entries require clicking the month arrow once per month, and an
    // afternoon time can't be entered in the morning. ampm={false} keeps the
    // clock on the same 24-hour scale as DATE_TIME_FORMAT.
    const views =
      type === "dateTime"
        ? ["year", "date", "hours", "minutes"]
        : ["year", "date"];

    const handleDateChange = (date) => {
      if (!date) {
        onChange(null);
      } else if (type === "date") {
        // Local calendar day, never toISOString(): the picker's Date carries a
        // time-of-day, and converting through UTC stores the next day for
        // evening edits (see parseDateOnly in utils).
        onChange(formatDateOnly(date)); // Store as "YYYY-MM-DD"
      } else {
        onChange(date.toISOString()); // Store as full ISO timestamp
      }
    };

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <FormControl
          fullWidth
          error={!!fieldState.error}
          style={getReadOnlyStyles(readOnly)}
        >
          <PickerComponent
            {...rest}
            ref={ref}
            disabled={readOnly}
            value={type === "date" ? parseDateOnly(value) : value || null}
            onChange={handleDateChange}
            format={formatString}
            inputVariant="outlined"
            variant="inline"
            autoOk
            views={views}
            openTo="date"
            ampm={false}
            style={getPointerEventsStyles(readOnly)}
            size="small"
            InputProps={{
              style: { color: "rgba(0, 0, 0, 0.87)" },
            }}
          />
          {!!fieldState.error && (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      </MuiPickersUtilsProvider>
    );
  }
);
